import type { TeamMemberWContact } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { MoreHorizontal } from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import { compareNoCase } from "@/lib/utils";
import {
  createAddressColumn,
  createExpirationDateColumn,
  createIdColumn,
  createPhoneColumn,
  createRoleColumn,
} from "@/components/dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { CLASSES, CLASSES_LABELS } from "@/lib/constants";
import { arrayify } from "@repo/shared/utils";

const createTitleColumn = (): ColumnDef<TeamMemberWContact> => ({
  accessorKey: "title",
  meta: {
    label: "Title",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Title" />
  ),
});

const createFirstNameColumn = (): ColumnDef<TeamMemberWContact> => ({
  accessorKey: "firstName",
  meta: {
    label: "First Name",
  },
  filterFn: (a, _, value: string) => {
    return a.original.contactInfo.firstName
      .toLowerCase()
      .includes(value.toLowerCase());
  },
  sortingFn: (a, b) =>
    compareNoCase(
      a.original.contactInfo.firstName,
      b.original.contactInfo.firstName
    ),
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="First Name" />
  ),
  cell: ({ row }) => {
    const teamMember = row.original;
    return teamMember.contactInfo.firstName;
  },
});

const createLastNameColumn = (): ColumnDef<TeamMemberWContact> => ({
  accessorKey: "lastName",
  meta: {
    label: "Last Name",
  },
  filterFn: (a, _, value: string) => {
    return a.original.contactInfo.lastName
      .toLowerCase()
      .includes(value.toLowerCase());
  },
  sortingFn: (a, b) =>
    compareNoCase(
      a.original.contactInfo.lastName,
      b.original.contactInfo.lastName
    ),
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Last Name" />
  ),
  cell: ({ row }) => {
    const teamMember = row.original;
    return teamMember.contactInfo.lastName;
  },
});

const createEmailColumn = (): ColumnDef<TeamMemberWContact> => ({
  accessorKey: "email",
  meta: {
    label: "Email",
  },
  filterFn: (a, _, value: string) => {
    return a.original.contactInfo.email
      .toLowerCase()
      .includes(value.toLowerCase());
  },
  sortingFn: (a, b) =>
    compareNoCase(a.original.contactInfo.email, b.original.contactInfo.email),
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Email" />
  ),
  cell: ({ row }) => {
    const teamMember = row.original;
    return teamMember.contactInfo.email;
  },
});

const createClassColumn = (): ColumnDef<TeamMemberWContact> => ({
  accessorKey: "class",
  meta: {
    label: "Class",
    filterVariant: "customSelect",
    customSelectOptions: CLASSES.map((cls) => ({
      label: CLASSES_LABELS[cls],
      value: cls,
    })),
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Class" />
  ),
  cell: ({ row }) => CLASSES_LABELS[row.original.class ?? "voting"],
});

export const useColumns = ({
  handleQuickEdit,
  handleDelete,
}: {
  handleQuickEdit: (id: string) => void;
  handleDelete: (id: string) => void;
}) => {
  handleQuickEdit = useFutureCallback(handleQuickEdit);
  handleDelete = useFutureCallback(handleDelete);

  const col: ColumnDef<TeamMemberWContact>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const teamMember = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleQuickEdit(teamMember.userId)}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDelete(teamMember.userId)}
                >
                  Remove
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigator.clipboard.writeText(teamMember.userId)
                  }
                >
                  Copy User ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createTitleColumn(),
      createRoleColumn<TeamMemberWContact>(),
      createClassColumn(),
      createFirstNameColumn(),
      createLastNameColumn(),
      createEmailColumn(),
      createAddressColumn((row) => row.contactInfo.primaryAddress),
      createPhoneColumn((row) =>
        row.contactInfo.primaryPhone
          ? arrayify(row.contactInfo.primaryPhone)
          : []
      ),
      createExpirationDateColumn("Term Expiration"),
    ],
    [handleQuickEdit, handleDelete]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
  },
};
